// Import dependencies
import { hot } from 'react-hot-loader';
import React from 'react';
import { Route, Routes, Navigate } from 'react-router-dom';
import './index.css';

// Import layout, urls, and theme
import Layout from './Layout.js';
import { APP_URLS } from './urls';
import { shades } from './theme';

// ------------------------
// Shared Scenes
// ------------------------
import Home from './src/Home';
import About from './src/scenes/about/About';
import Contact from './src/scenes/contact/Contact';
import ComingSoon from './src/components/comingSoon/index.jsx';

// ------------------------
// Patient-related imports
import PatientAuthPage from './src/scenes/user/patient/AuthPage.js';
import PatientProfileCompletion from './src/scenes/user/patient/ProfileCompletePage.js';
import PatientSettings from './src/scenes/user/patient/SettingsPage.js';
import PatientDashboard from './src/scenes/user/patient/dashboard_sections/PatientDashboard.js';
import PatientAssistant from './src/components/gpt/Gpt.jsx';
import PatientProfile from './src/components/patient/personal/profile.jsx';
import PatientUniversalMedicalRecord from './src/components/patient/medical_records/universal_medical_book.jsx';
// import patientNotifications from './src/components/patient/personal/notification.jsx';
import PatientNotifications from './src/scenes/user/patient/dashboard_sections/NotificationsPage.js';
import PatientReminder from './src/components/patient/personal/reminder.jsx';
import PatientWarning from './src/components/patient/personal/warning.jsx';
import PatientRefill from './src/components/patient/personal/refill.jsx';
import PatientPurchases from './src/scenes/user/patient/dashboard_sections/MedicalPurchasesPage.js';
import PatientTreatments from './src/scenes/user/patient/dashboard_sections/TreatmentHistoryPage.js';
import PatientShare from './src/scenes/user/patient/dashboard_sections/ShareMedicalDataPage.js';

// Dashboard Layout that includes the Sidebar
import PatientDashboardLayout from './src/components/patient/layouts/DashboardLayout';

// ------------------------
// Pharmacy-related imports
// ------------------------
import PharmacyLogin from './src/scenes/user/pharmacy/pharmacyAuth.js';
import PharmacyRegister from './src/scenes/user/pharmacy/pharmacyAuth.js';
import ResetPassword from './src/scenes/user/pharmacy/resetPassword.js';
import PharmacyDashboard from './src/scenes/user/pharmacy/dashboard.js';
import PharmacyForgotPassword from './src/scenes/user/pharmacy/forgotPasswordPage.js';
import ProfileCompletionWizard from './src/scenes/user/pharmacy/profile.js';

// ------------------------
// Hospital-related imports
// ------------------------
import HospitalAuthPage from './src/scenes/user/hospital/hospitalAuth.js';
import HospitalForgotPassword from './src/scenes/user/hospital/forgotPasswordPage.js';
import HospitalResetPassword from './src/scenes/user/hospital/resetPasswordPage.js';
import HospitalProfile from './src/scenes/user/hospital/profilecomplete.js';
import DashboardHome from './src/scenes/user/hospital/dashboard.js';

// ------------------------
// Doctor-related imports
// ------------------------
import DoctorRegister from './src/scenes/user/doctor/DoctorRegister';
import DoctorLogin from './src/scenes/user/doctor/login';
import DoctorResetPassword from './src/scenes/user/doctor/resetPassword';
import DoctorVerifyEmail from './src/scenes/user/doctor/verifyEmail';
import ProfileCompletion from './src/scenes/user/doctor/profilecompletion';
import DoctorDashboard from './src/scenes/user/doctor/profile/dashboard.js';
import PendingApproval from './src/scenes/user/doctor/profile/pendingApproval.js';

// ------------------------
// Medications and Cart imports
// ------------------------
import Listings from './src/components/medications/Listing';
import Inventory from './src/components/medications/Inventory/Inventory.js';
import Item from './src/components/medications/Item';
import DrugUpload from './src/components/medications/drugUpload';
import ShoppingCart from './src/scenes/cart/ShoppingCart.jsx';
import Checkout from './src/scenes/Checkout.js';

// ------------------------
// Medichat feature imports
// ------------------------
import HomeFeeds from './src/scenes/medichat/HomeFeeds/Feeds';
import Groups from './src/scenes/medichat/Groups/MediChatGroups.js';
import HomePages from './src/scenes/medichat/Pages/Pages.js';
import ProfilePage from './src/scenes/medichat/profile/Profile.js';

// ------------------------
// Application Component
// ------------------------
function App() {
	return (
		<Routes>
			<Route
				path="*"
				element={
					<Layout>
						<Routes>
							{/* General pages */}
							<Route path={APP_URLS.home} element={<Home />} />
							<Route path={APP_URLS.about} element={<About />} />
							<Route path={APP_URLS.contact} element={<Contact />} />
							<Route path={APP_URLS.comingSoon} element={<ComingSoon />} />
							{/* Patient-specific routes */}
							{/* Authentication pages – outside the PatientDashboardLayout */}
							<Route
								path={APP_URLS.patientLogin}
								element={<PatientAuthPage initialFormType="login" />}
							/>
							<Route
								path={APP_URLS.patientRegister}
								element={<PatientAuthPage initialFormType="register" />}
							/>
							<Route
								path={APP_URLS.patientForgotPassword}
								element={<PatientAuthPage initialFormType="forgotPassword" />}
							/>
							<Route
								path={APP_URLS.patientResetPassword}
								element={<PatientAuthPage initialFormType="resetPassword" />}
							/>{' '}
							{/* Patient Dashboard – wrapped in PatientDashboardLayout */}
							<Route element={<PatientDashboardLayout />}>
								<Route
									path={APP_URLS.patientDashboard}
									element={<PatientDashboard />}
								/>
								<Route
									path={APP_URLS.patientSettings}
									element={<PatientSettings />}
								/>
								<Route
									path={APP_URLS.patientUniversalMedicalRecord}
									element={<PatientUniversalMedicalRecord />}
								/>
								<Route
									path={APP_URLS.patientNotifications}
									element={<PatientNotifications />}
								/>
								<Route
									path={APP_URLS.patientRefill}
									element={<PatientRefill />}
								/>
								<Route
									path={APP_URLS.patientReminder}
									element={<PatientReminder />}
								/>
								<Route
									path={APP_URLS.patientWarning}
									element={<PatientWarning />}
								/>
								<Route
									path={APP_URLS.patientProfile}
									element={<PatientProfile />}
								/>
								<Route
									path={APP_URLS.patientAssistant}
									element={<PatientAssistant />}
								/>
								<Route
									path={APP_URLS.patientPurchases}
									element={<PatientPurchases />}
								/>
								<Route
									path={APP_URLS.patientTreatments}
									element={<PatientTreatments />}
								/>
								<Route
									path={APP_URLS.patientShare}
									element={<PatientShare />}
								/>
								<Route
									path={APP_URLS.patientProfileCompletion}
									element={<PatientProfileCompletion />}
								/>
							</Route>
							{/* Pharmacy-specific routes */}
							<Route
								path={APP_URLS.pharmacyRegister}
								element={<PharmacyRegister />}
							/>
							<Route
								path={APP_URLS.pharmacyLogin}
								element={<PharmacyLogin />}
							/>
							<Route
								path={APP_URLS.pharmacyDashboard}
								element={<PharmacyDashboard />}
							/>
							<Route
								path={APP_URLS.pharmacyResetPassword}
								element={<ResetPassword />}
							/>
							<Route
								path={APP_URLS.pharmacyForgotPassword}
								element={<PharmacyForgotPassword />}
							/>
							<Route
								path={APP_URLS.pharmacyProfileCompletion}
								element={<ProfileCompletionWizard />}
							/>
							{/* Hospital-specific routes */}
							<Route
								path={APP_URLS.hospitalLogin}
								element={<HospitalAuthPage initialFormType="login" />}
							/>
							<Route
								path={APP_URLS.hospitalVerification}
								element={<HospitalAuthPage initialFormType="login" />}
							/>
							<Route
								path={APP_URLS.hospitalRegister}
								element={<HospitalAuthPage initialFormType="register" />}
							/>
							<Route
								path={APP_URLS.hospitalDashboard}
								element={<DashboardHome />}
							/>
							<Route
								path={APP_URLS.hospitalProfile}
								element={<HospitalProfile />}
							/>
							<Route
								path={APP_URLS.hospitalForgotPassword}
								element={<HospitalForgotPassword />}
							/>
							<Route
								path={APP_URLS.hospitalResetPassword}
								element={<HospitalResetPassword />}
							/>
							{/* Doctor-specific routes */}
							<Route
								path={APP_URLS.doctorRegister}
								element={<DoctorRegister />}
							/>
							<Route path={APP_URLS.doctorLogin} element={<DoctorLogin />} />
							<Route
								path={APP_URLS.doctorResetPassword}
								element={<DoctorResetPassword />}
							/>
							<Route
								path={APP_URLS.doctorVerifyEmail}
								element={<DoctorVerifyEmail />}
							/>
							<Route
								path={APP_URLS.profileCompletion}
								element={<ProfileCompletion />}
							/>
							<Route
								path={APP_URLS.doctorPendingApproval}
								element={<PendingApproval />}
							/>
							<Route
								path={APP_URLS.doctorsDashboard}
								element={<DoctorDashboard />}
							/>
							{/* Medications and Cart routes */}
							<Route path={APP_URLS.products} element={<Listings />} />
							<Route path={APP_URLS.item} element={<Item />} />
							<Route path={APP_URLS.inventory} element={<Inventory />} />
							<Route path={APP_URLS.drugsUpload} element={<DrugUpload />} />
							<Route path={APP_URLS.shoppingCart} element={<ShoppingCart />} />
							<Route path={APP_URLS.checkout} element={<Checkout />} />
							{/* Medichat routes */}
							<Route
								path={APP_URLS.mediChatMessages}
								element={<ComingSoon />}
							/>
							<Route path={APP_URLS.mediChatHome} element={<HomeFeeds />} />
							<Route path={APP_URLS.mediChatGroups} element={<Groups />} />
							<Route path={APP_URLS.mediChatPages} element={<HomePages />} />
							<Route
								path={APP_URLS.mediChatProfile}
								element={<ProfilePage />}
							/>
							{/* Fallback route */}
							<Route path="*" element={<Navigate to={APP_URLS.home} />} />
						</Routes>
					</Layout>
				}
			/>
		</Routes>
	);
}

// Export the App component wrapped with hot-reloading
export default hot(module)(App);
